import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import { useCallback, useState } from "react";
import { api } from "@/api";
import { useForm } from "react-hook-form";
import FileUpload from "./fileUpload";
import useErrorHandling from "@/hooks/useErrorHandling";
import { toast } from "react-toastify";

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFileUploaded: (urls: string[]) => void;
  title?: string;
  accept?: string;
  multiple?: boolean;
}

export function FileUploadModal({
  isOpen,
  onClose,
  onFileUploaded,
  title = "Загрузить файл",
  accept = "image/*",
  multiple = false,
}: FileUploadModalProps) {
  const [isUploading, setIsUploading] = useState(false);
  const handleError = useErrorHandling();
  const { control, watch } = useForm();

  const files = watch("files");

  const handleUpload = useCallback(async () => {
    if (!files) return;

    setIsUploading(true);

    try {
      const fileArray = Array.isArray(files) ? files : [files];
      const uploadPromises = fileArray.map((file) => api.s3.uploadFile(file));

      const responses = await Promise.all(uploadPromises);
      const urls = responses
        .map((response) => response.data)
        .filter(Boolean) as string[];

      if (urls.length > 0) {
        onFileUploaded(urls);
        onClose();
      }
    } catch (error) {
      const parsedError = handleError(error as unknown as Error);
      toast.error(parsedError.errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [files, onFileUploaded, onClose, handleError]);

  const hasFiles = files && (Array.isArray(files) ? files.length > 0 : true);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      classNames={{
        backdrop: "z-[500]",
        wrapper: "z-[500]",
      }}
      backdrop="blur"
      scrollBehavior="outside"
      isDismissable={false}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {multiple ? "Загрузить файлы" : title}
        </ModalHeader>
        <ModalBody>
          <FileUpload
            control={control}
            name="files"
            accept={accept}
            multiple={multiple}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            Отмена
          </Button>
          <Button
            color="primary"
            onPress={handleUpload}
            isLoading={isUploading}
            isDisabled={!hasFiles || isUploading}
          >
            {multiple ? "Загрузить файлы" : "Загрузить"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
